// AEOLL-HOOK:BEGIN api.files.diagnostics-import
import { beginDiagnosticFileTransfer } from '$lib/aeoll/diagnostics/fileTransferBrowser';
// AEOLL-HOOK:END api.files.diagnostics-import
import { WEBUI_API_BASE_URL } from '$lib/constants';
import { splitStream } from '$lib/utils';

export const uploadFile = async (
	token: string,
	file: File,
	metadata?: object | null,
	process?: boolean | null
) => {
	// AEOLL-HOOK:BEGIN api.files.diagnostics-begin
	const diagnosticTransfer = beginDiagnosticFileTransfer(token, file);
	// AEOLL-HOOK:END api.files.diagnostics-begin
	const data = new FormData();
	data.append('file', file);
	if (metadata) {
		data.append('metadata', JSON.stringify(metadata));
	}

	const searchParams = new URLSearchParams();
	if (process !== undefined && process !== null) {
		searchParams.append('process', String(process));
	}

	let error = null;

	// AEOLL-HOOK:BEGIN api.files.diagnostics-transfer
	const res = await diagnosticTransfer
		.fetch(() =>
			fetch(`${WEBUI_API_BASE_URL}/files/?${searchParams.toString()}`, {
				method: 'POST',
				headers: {
					...diagnosticTransfer.headers,
					Accept: 'application/json',
					authorization: `Bearer ${token}`
				},
				body: data
			})
		)
		// AEOLL-HOOK:END api.files.diagnostics-transfer
		.then(async (res) => {
			// AEOLL-HOOK:BEGIN api.files.diagnostics-rejection-body
			if (!res.ok) throw await diagnosticTransfer.observe('receipt_body', () => res.json());
			// AEOLL-HOOK:END api.files.diagnostics-rejection-body
			// AEOLL-HOOK:BEGIN api.files.diagnostics-accepted
			const value = await diagnosticTransfer.observe('receipt_body', () => res.json());
			diagnosticTransfer.accepted(value);
			return value;
			// AEOLL-HOOK:END api.files.diagnostics-accepted
		})
		.catch((err) => {
			error = err.detail || err.message;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	if (res) {
		// AEOLL-HOOK:BEGIN api.files.diagnostics-status-request
		const status = await diagnosticTransfer.observe('status_request', () =>
			getFileProcessStatus(token, res.id, (error) =>
				diagnosticTransfer.observationLost('status_request', error)
			)
		);
		// AEOLL-HOOK:END api.files.diagnostics-status-request

		if (status && status.ok) {
			const reader = status.body
				.pipeThrough(new TextDecoderStream())
				.pipeThrough(splitStream('\n'))
				.getReader();

			while (true) {
				// AEOLL-HOOK:BEGIN api.files.diagnostics-status-read
				const { value, done } = await diagnosticTransfer.observe('status_stream', () =>
					reader.read()
				);
				// AEOLL-HOOK:END api.files.diagnostics-status-read
				if (done) {
					break;
				}

				try {
					let lines = value.split('\n');

					for (const line of lines) {
						if (line !== '') {
							console.log(line);
							if (line === 'data: [DONE]') {
								console.log(line);
							} else {
								let data = JSON.parse(line.replace(/^data: /, ''));
								// AEOLL-HOOK:BEGIN api.files.diagnostics-processing
								diagnosticTransfer.processing(data);
								// AEOLL-HOOK:END api.files.diagnostics-processing
								console.log(data);

								if (data?.error) {
									console.error(data.error);
									res.error = data.error;
								}

								if (res?.data) {
									res.data = data;
								}
							}
						}
					}
				} catch (error) {
					// AEOLL-HOOK:BEGIN api.files.diagnostics-status-parse
					diagnosticTransfer.observationLost('status_parse', error);
					// AEOLL-HOOK:END api.files.diagnostics-status-parse
					console.log(error);
				}
			}
		}
	}

	if (error) {
		throw error;
	}

	return res;
};

// AEOLL-HOOK:BEGIN api.files.diagnostics-status-error-port
export const getFileProcessStatus = async (
	token: string,
	id: string,
	onObservationError?: (error: unknown) => void
) => {
	// AEOLL-HOOK:END api.files.diagnostics-status-error-port
	const queryParams = new URLSearchParams();
	queryParams.append('stream', 'true');

	let error = null;
	const res = await fetch(`${WEBUI_API_BASE_URL}/files/${id}/process/status?${queryParams}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			authorization: `Bearer ${token}`
		}
	}).catch((err) => {
		// AEOLL-HOOK:BEGIN api.files.diagnostics-status-error
		onObservationError?.(err);
		// AEOLL-HOOK:END api.files.diagnostics-status-error
		error = err.detail;
		console.error(err);
		return null;
	});

	if (error) {
		throw error;
	}

	return res;
};

export const uploadDir = async (token: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/upload/dir`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.catch((err) => {
			error = err.detail;
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const getFiles = async (token: string = '') => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.then((json) => {
			return json;
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const searchFiles = async (
	token: string,
	filename: string = '*',
	skip: number = 0,
	limit: number = 50
) => {
	let error = null;

	const searchParams = new URLSearchParams();
	searchParams.append('filename', filename);
	searchParams.append('skip', String(skip));
	searchParams.append('limit', String(limit));

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/search?${searchParams.toString()}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return [];
		});

	if (error) {
		throw error;
	}

	return res;
};

export const getFileById = async (token: string, id: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/${id}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.then((json) => {
			return json;
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const updateFileDataContentById = async (token: string, id: string, content: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/${id}/data/content/update`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		},
		body: JSON.stringify({
			content: content
		})
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.then((json) => {
			return json;
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const getFileContentById = async (id: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/${id}/content`, {
		method: 'GET',
		headers: {
			Accept: 'application/json'
		},
		credentials: 'include'
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return await res.arrayBuffer();
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);

			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const deleteFileById = async (token: string, id: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/${id}`, {
		method: 'DELETE',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.then((json) => {
			return json;
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};

export const deleteAllFiles = async (token: string) => {
	let error = null;

	const res = await fetch(`${WEBUI_API_BASE_URL}/files/all`, {
		method: 'DELETE',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			authorization: `Bearer ${token}`
		}
	})
		.then(async (res) => {
			if (!res.ok) throw await res.json();
			return res.json();
		})
		.then((json) => {
			return json;
		})
		.catch((err) => {
			error = err.detail;
			console.error(err);
			return null;
		});

	if (error) {
		throw error;
	}

	return res;
};
